/**
 * clarifyingBreadcrumb.ts — INTERSTITIAL-MESSAGE ELIMINATION (owner directive
 * 2026-08-13).
 *
 * The deep-research Stage-1 clarifying turn's tool `output` is a one-line
 * skip breadcrumb ("Clarifying questions ready — tap **Skip to Deep
 * Research** to proceed, or reply with your answers."). It exists ONLY as an
 * internal anti-loop / non-empty-output artifact on the backend
 * (functions/src/prest0nSession/clarifyingPreStage.ts SKIP_INSTRUCTION_FOOTER)
 * and must NEVER render as a standalone assistant chat message: the
 * structured clarifying card is the turn's one visible surface, and the
 * turn's token usage attaches to that card — the most recent real message.
 *
 * The backend no longer ships the breadcrumb as `surfaceOwnedText` /
 * durable message body on any lane, but task docs written by PRE-FIX
 * servers (and any lane hit during a mixed-version rollout) can still carry
 * it. This module is the client-side detector every message-creating path
 * uses to refuse to materialize that text as a message body:
 *
 *   - useGlobalTools onDone orphan branch (handleOrphanResearchDeliverableDone)
 *   - serverTaskResume.decideAuthedResumeFinalize (refresh-resume finalize)
 *
 * Signatures mirror clarifyingPreStage.ts exactly (current one-liner EN/ES +
 * the legacy PR #194 footer). Keep the two lists in lockstep — the strings
 * are pinned by tests on both sides.
 *
 * Pure module: no React, no firebase, no I/O.
 */

/** Stable substrings of the CURRENT clarifying one-liner (EN/ES). */
export const CLARIFYING_SKIP_FOOTER_SIGNATURE_EN = 'tap **Skip to Deep Research** to proceed';
export const CLARIFYING_SKIP_FOOTER_SIGNATURE_ES = 'toca **Ir directo a la investigación** para continuar';
/** Legacy PR #194 verbose-card footers — still present in old thread docs. */
export const LEGACY_CLARIFYING_SKIP_FOOTER_SIGNATURE_EN = 'Click **Skip to Deep Research** above';
export const LEGACY_CLARIFYING_SKIP_FOOTER_SIGNATURE_ES = 'Haz clic en **Ir directo a la investigación** arriba';

const ALL_SIGNATURES = [
  CLARIFYING_SKIP_FOOTER_SIGNATURE_EN,
  CLARIFYING_SKIP_FOOTER_SIGNATURE_ES,
  LEGACY_CLARIFYING_SKIP_FOOTER_SIGNATURE_EN,
  LEGACY_CLARIFYING_SKIP_FOOTER_SIGNATURE_ES,
];

/** True when `text` contains any clarifying skip-breadcrumb signature. */
export function containsClarifyingSkipBreadcrumb(text: unknown): boolean {
  if (typeof text !== 'string' || !text) return false;
  return ALL_SIGNATURES.some((sig) => text.includes(sig));
}

/**
 * True when `text` IS the breadcrumb (as opposed to merely embedding it in a
 * larger real body): short, and carries a signature. The length ceiling is
 * generous — the one-liner is ~110 chars in both languages — so a genuine
 * report body that happens to quote the footer is never suppressed.
 */
export function isClarifyingSkipBreadcrumbBody(text: unknown): boolean {
  if (typeof text !== 'string') return false;
  const trimmed = text.trim();
  if (!trimmed || trimmed.length > 400) return false;
  return containsClarifyingSkipBreadcrumb(trimmed);
}

/**
 * Deep-research tool paths whose surface is an INTERACTIVE clarifying card —
 * the turn must not produce any standalone text message. Mirrors
 * functions/src/prest0nSession/clarifyingPreStage.ts
 * isInteractiveClarifyingDeepResearchPath.
 */
export function isInteractiveClarifyingDeepResearchPath(path: unknown): boolean {
  const normalized = String(path || '');
  return normalized === 'clarifying-questions' || normalized === 'fast-clarify';
}

/**
 * Resolve the EFFECTIVE deep-research path of a persisted turn from its
 * toolsInvoked list: the LAST deep-research entry carrying a non-empty
 * metadata.path. Latest-wins, mirroring the backend's surface decision
 * (runtime.findLatestSurfaceOwnedDeepResearchResult) — a leading failed
 * deep-research invocation with no metadata must never mask the retry that
 * actually produced the turn's surface (adversarial-review finding,
 * 2026-08-13). Returns '' when no entry carries a path.
 */
export function resolveLatestDeepResearchPath(toolsInvoked: unknown): string {
  if (!Array.isArray(toolsInvoked)) return '';
  for (let i = toolsInvoked.length - 1; i >= 0; i -= 1) {
    const tool = toolsInvoked[i] as { toolName?: unknown; metadata?: unknown } | null | undefined;
    if (!tool || tool.toolName !== 'deep-research') continue;
    const metadata = (tool.metadata && typeof tool.metadata === 'object')
      ? (tool.metadata as Record<string, unknown>)
      : undefined;
    const path = typeof metadata?.path === 'string' ? metadata.path : '';
    if (path) return path;
  }
  return '';
}
